<!-- eslint-disable vue/no-v-html -->
<template>
  <!-- Vue app instance - mounts directly to this div -->
  <template v-if="vueAppInstance">
    <div ref="appMountPoint" :class="[className, anchor]" v-bind="$attrs"></div>
  </template>

  <!-- Vue component with props and children and template literal support -->
  <div v-else-if="vueComponent" :class="[className, anchor]">
    <component :is="vueComponent" v-bind="vueComponentProps">
      <template v-if="templateLiteral">
        <div v-bind="$attrs" v-html="templateLiteral" />
      </template>
      <template v-else-if="typeof vueComponentChildren === 'function'">
        {{ vueComponentChildren() }}
      </template>
      <template v-else-if="typeof vueComponentChildren === 'string'">
        {{ vueComponentChildren }}
      </template>
      <template v-else>
        <component :is="vueComponentChildren" v-if="vueComponentChildren" />
      </template>
    </component>
  </div>

  <!-- HTML string -->
  <div
    v-else-if="templateLiteral"
    :class="[className, anchor]"
    v-bind="$attrs"
    v-html="templateLiteral"
  />
</template>

<script setup lang="ts">
/**
 * MapElementWrapper
 *
 * Transforms multiple Vue and non-Vue content types into a single, consistent Vue Component.
 * Acts as an adapter that accepts Vue app instances (App<Element>), Vue components,
 * or raw HTML strings and renders them uniformly.
 *
 * ## Examples
 *
 * ### Example 1: Render a Vue app instance (from createApp)
 * <map-element-wrapper
 *   :vue-app-instance="myAppInstance"
 *   class-name="popup-content"
 * />
 *
 * ### Example 2: Render a Vue component with props
 * <map-element-wrapper
 *   :vue-component="MyComponent"
 *   :vue-component-props="{ title: 'Hello' }"
 * />
 *
 * ### Example 3: Render HTML template string
 * <map-element-wrapper
 *   template-literal="<div>Hello World</div>"
 *   class-name="static-content"
 * />
 *
 * ### Example 4: Component with children and optional positioning
 * <map-element-wrapper
 *   :vue-component="Container"
 *   :vue-component-children="ChildComponent"
 *   class-name="anchor-offset"
 *   anchor="bottom"
 *   :offset="10"
 * />
 *
 * ### Example 5: Component as parameter, rendering a Vue component and nested reactive HTML template content
 *  const contextMenuElement = createHTMLElement(MapElementWrapper, {
 *    vueComponent: XToolTip,
 *    vueComponentProps: {
 *      trigger: 'always',
 *      delay: 0,
 *      direction: 'top',
 *      style: 'pointer-events: initial;',
 *    },
 *    templateLiteral: contextMenuContentTemplate.value,
 *  }
 *
 * ## Anchor-based Positioning
 * When className includes "anchor-offset", CSS transforms apply positioning
 * based on anchor (top/bottom/center) and offset values.
 */
import type { Popup } from '@/services/map-service'
import { ref, onMounted, markRaw, computed } from 'vue'
import type { Component, VNode, App, VNodeArrayChildren } from 'vue'

type RawChildren = string | VNode | VNodeArrayChildren | (() => any)

interface Props {
  /** Vue app instance created with createApp() - mutually exclusive with vueComponent and templateLiteral */
  vueAppInstance?: App<Element> | null
  /** Vue component to render - mutually exclusive with vueAppInstance and standalone templateLiteral */
  vueComponent?: Component | null
  /** Props to pass to the Vue component */
  vueComponentProps?: Record<string, any> | null
  /** Children content: string, function, or component */
  vueComponentChildren?: RawChildren
  /** HTML string to render - can be standalone or nested within vueComponent */
  templateLiteral?: string
  /** CSS classes applied to wrapper element */
  className?: string | string[]
  /**
   * Anchor positioning determines which edge of the element is pinned to the reference point
   * - 'top': Top edge of element aligns with point, element extends downward
   * - 'bottom': Bottom edge of element aligns with point, element extends upward
   * - 'center': Center of element aligns with point
   * Requires "anchor-offset" in className to apply transforms
   */
  anchor?: Popup['anchor']
  /**
   * Offset is the gap distance (in pixels) between the anchor point and the element's edge
   * Positive values move element away from anchor point, negative values move toward it:
   * - 'top': positive offset moves element down (away), negative moves up (toward)
   * - 'bottom': positive offset moves element up (away), negative moves down (toward)
   * - 'center': positive offset moves element up, negative moves element down (vertical adjustment)
   * Requires "anchor-offset" in className to apply
   */
  offset?: Popup['offset']
}

const props = defineProps<Props>()
const appMountPoint = ref<HTMLElement>()
const computedOffset = computed<string>(() => {
  if (!props.offset) {
    return '0px'
  }

  const offsetValue = typeof props.offset === 'number' ? props.offset : 0

  // Apply directional offset based on anchor
  // Positive offset moves away from point, negative moves toward point
  switch (props.anchor) {
    case 'top':
      // Top anchor: positive offset moves DOWN (away), negative moves UP (toward)
      return `${offsetValue}px`
    case 'bottom':
      // Bottom anchor: positive offset moves UP (away), negative moves DOWN (toward)
      return `${-offsetValue}px`
    case 'center':
      // Center anchor: positive offset moves UP, negative moves DOWN (vertical adjustment)
      return `${-offsetValue}px`
    default:
      return '0px'
  }
})

onMounted(() => {
  if (props.vueAppInstance && appMountPoint.value) {
    markRaw(props.vueAppInstance)

    if (props.vueAppInstance._container) {
      props.vueAppInstance.unmount()
    }

    props.vueAppInstance.mount(appMountPoint.value)
  }
})
</script>

<style scoped lang="scss">
/*
 * Optional anchor-based positioning utilities.
 * Apply transforms relative to a reference point using anchor classes.
 */
.anchor-offset {
  &.bottom {
    // Bottom anchor: element's bottom edge at point, offset moves it away (up)
    transform: translate(-50%, calc(-100% + v-bind(computedOffset)));
  }

  &.top {
    // Top anchor: element's top edge at point, offset moves it away (down)
    transform: translate(-50%, v-bind(computedOffset));
  }

  &.center {
    // Center anchor: element centered at point, offset provides vertical adjustment
    transform: translate(-50%, calc(-50% + v-bind(computedOffset)));
  }
}
</style>
